<template>
<v-container fluid class="page-wrap">
    <v-row>
      <v-col cols="12" class="text-center">
          <header-vue/>
      </v-col>

      <v-col class="mb-5" md="12">
            <v-card class="rounded-xl pa-2 pa-md-3" elevation="3">
                <v-card-title class="page-title-row">
                    <div>
                        <div class="text-overline accent--text font-weight-bold">ATTENDANCE</div>
                        <div class="text-h6 font-weight-bold primary--text">Teacher Attendance Summary</div>
                        <div class="text-body-2 grey--text">Track punctual, late and absent days for teaching staff</div>
                    </div>
                </v-card-title>
                <v-card-text>
                    <v-row align="center">
                        <v-col cols="12" md="3">
                            <v-menu v-model="monthMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        :value="formatMonthLabel(month)"
                                        label="Month"
                                        outlined dense readonly
                                        prepend-inner-icon="mdi-calendar-month"
                                        v-bind="attrs" v-on="on"
                                    ></v-text-field>
                                </template>
                                <v-date-picker v-model="month" type="month" no-title @input="monthMenu = false"></v-date-picker>
                            </v-menu>
                        </v-col>

                        <v-col cols="12" md="4" v-if="isAdmin && teachers.length">
                            <v-select
                                v-model="selectedTeacherId"
                                :items="teachers"
                                item-text="fullName"
                                item-value="id"
                                label="Staff Member"
                                outlined dense
                                prepend-inner-icon="mdi-account-tie"
                            ></v-select>
                        </v-col>
                    </v-row>

                    <info-loader-2 v-if="fetchingContent"/>

                    <template v-else>
                        <v-alert v-if="errorMessage" type="warning" text border="left">{{ errorMessage }}</v-alert>

                        <template v-else>
                            <div class="text-subtitle-1 font-weight-bold primary--text mb-2" v-if="summary.teacher">
                                {{ summary.teacher.firstname }} {{ summary.teacher.lastname }} &mdash; {{ formatMonthLabel(month) }}
                            </div>

                            <v-row>
                                <v-col cols="6" sm="3">
                                    <v-card outlined class="rounded-lg pa-4 text-center">
                                        <v-icon color="green" size="28">mdi-calendar-check</v-icon>
                                        <div class="text-h6 font-weight-bold">{{ summary.present }}</div>
                                        <div class="text-caption grey--text">Present</div>
                                    </v-card>
                                </v-col>
                                <v-col cols="6" sm="3">
                                    <v-card outlined class="rounded-lg pa-4 text-center">
                                        <v-icon color="green darken-2" size="28">mdi-check-circle</v-icon>
                                        <div class="text-h6 font-weight-bold">{{ summary.punctual }}</div>
                                        <div class="text-caption grey--text">Punctual</div>
                                    </v-card>
                                </v-col>
                                <v-col cols="6" sm="3">
                                    <v-card outlined class="rounded-lg pa-4 text-center">
                                        <v-icon color="orange" size="28">mdi-alert-circle</v-icon>
                                        <div class="text-h6 font-weight-bold">{{ summary.late }}</div>
                                        <div class="text-caption grey--text">Late</div>
                                    </v-card>
                                </v-col>
                                <v-col cols="6" sm="3">
                                    <v-card outlined class="rounded-lg pa-4 text-center">
                                        <v-icon color="red" size="28">mdi-close-circle</v-icon>
                                        <div class="text-h6 font-weight-bold">{{ summary.absent }}</div>
                                        <div class="text-caption grey--text">Absent</div>
                                    </v-card>
                                </v-col>
                            </v-row>

                            <v-card outlined class="rounded-lg mt-4">
                                <v-card-title class="text-subtitle-1 font-weight-bold primary--text">Daily Log</v-card-title>
                                <v-data-table
                                    :headers="logHeaders"
                                    :items="summary.logs"
                                    class="elevation-0"
                                    dense
                                    disable-pagination
                                    hide-default-footer
                                    no-data-text="No clock-in/out records for this month yet"
                                >
                                    <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
                                    <template v-slot:item.clock_in="{ item }">
                                        <span v-if="item.clock_in">{{ formatTime(item.clock_in) }}</span>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template v-slot:item.status="{ item }">
                                        <v-chip v-if="item.status" :color="item.status === 'punctual' ? 'green' : 'orange'" dense dark x-small>{{ item.status }}</v-chip>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                    <template v-slot:item.clock_out="{ item }">
                                        <span v-if="item.clock_out">{{ formatTime(item.clock_out) }}</span>
                                        <span v-else class="grey--text">-</span>
                                    </template>
                                </v-data-table>
                            </v-card>
                        </template>
                    </template>
                </v-card-text>
            </v-card>
      </v-col>
    </v-row>

    <v-row v-if="isAdmin">
        <v-col cols="12">
            <v-card class="rounded-xl pa-2 pa-md-3" elevation="3">
                <v-card-title class="text-subtitle-1 font-weight-bold primary--text">
                    Today's Staff Clock Status
                </v-card-title>
                <v-card-text>
                    <v-data-table
                        :headers="overviewHeaders"
                        :items="overviewRecords"
                        class="elevation-0"
                        dense
                        disable-pagination
                        hide-default-footer
                        no-data-text="No active staff records found"
                    >
                        <template v-slot:item.clock_in="{ item }">
                            <span v-if="item.clock_in">{{ formatTime(item.clock_in) }}</span>
                            <span v-else class="grey--text">Not clocked in</span>
                        </template>
                        <template v-slot:item.clock_in_status="{ item }">
                            <v-chip v-if="item.clock_in_status" :color="item.clock_in_status === 'punctual' ? 'green' : 'orange'" dense dark x-small>{{ item.clock_in_status }}</v-chip>
                            <span v-else class="grey--text">-</span>
                        </template>
                        <template v-slot:item.clock_out="{ item }">
                            <span v-if="item.clock_out">{{ formatTime(item.clock_out) }}</span>
                            <span v-else class="grey--text">-</span>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import HeaderVue from '../../components/Header.vue'
import InfoLoader2 from '../../components/InfoLoader2.vue';

export default {
    name: 'TeacherClockSummary',
    components: {
        HeaderVue,
        InfoLoader2,
    },
    data: () => ({
        authToken: sessionStorage.getItem('authToken') ?? null,
        accessToken: sessionStorage.getItem('accessToken') ?? null,
        school: localStorage.getItem('school') ? JSON.parse(localStorage.getItem('school')) : {},
        month: moment().format('YYYY-MM'),
        monthMenu: false,
        isAdmin: false,
        teachers: [],
        selectedTeacherId: null,
        fetchingContent: true,
        errorMessage: '',
        summary: { teacher: null, punctual: 0, late: 0, present: 0, absent: 0, logs: [] },
        overviewRecords: [],
        logHeaders: [
            { text: 'Date', value: 'date' },
            { text: 'Clock In', value: 'clock_in' },
            { text: 'Status', value: 'status' },
            { text: 'Clock Out', value: 'clock_out' },
        ],
        overviewHeaders: [
            { text: 'Staff', value: 'name' },
            { text: 'Clock In', value: 'clock_in' },
            { text: 'Status', value: 'clock_in_status' },
            { text: 'Clock Out', value: 'clock_out' },
        ],
    }),
    watch: {
        month() {
            this.fetchSummary()
        },
        selectedTeacherId() {
            this.fetchSummary()
        },
    },
    mounted() {
        if (!this.authToken) {
            this.$router.push({name: 'Login'})
        }
        this.checkAdmin().finally(() => {
            this.fetchSummary()
        })
    },
    methods: {
        checkAdmin() {
            return axios.get(process.env.VUE_APP_BASEURL+'/attendances/overview?school_id='+this.school.school_id,
            {
                headers: { 'Authorization': 'Bearer '+this.authToken, 'X-Access-Token': this.accessToken }
            })
            .then((response) => {
                this.isAdmin = true
                this.overviewRecords = response.data.records
                return this.fetchTeachers()
            })
            .catch(() => {
                this.isAdmin = false
            })
        },
        fetchTeachers() {
            return axios.get(process.env.VUE_APP_BASEURL+'/teachers?school_id='+this.school.school_id,
            {
                headers: { 'Authorization': 'Bearer '+this.authToken, 'X-Access-Token': this.accessToken }
            })
            .then((response) => {
                this.teachers = response.data.data
                    .filter(t => t.status === 'active')
                    .map(t => ({ ...t, fullName: [t.firstname, t.middlename, t.lastname].filter(Boolean).join(' ') }))
            })
            .catch((error) => {
                this.$toast.error(error.response ? error.response.data.message : 'APP is temporarily unavailable. Wait or Contact us!', {
                    timeout: 5000
                });
            })
        },
        fetchSummary() {
            this.fetchingContent = true
            this.errorMessage = ''
            let url = process.env.VUE_APP_BASEURL+'/attendances/teacher-summary?school_id='+this.school.school_id+'&month='+this.month
            if (this.selectedTeacherId) {
                url += '&teacher_id='+this.selectedTeacherId
            }
            axios.get(url, {
                headers: { 'Authorization': 'Bearer '+this.authToken, 'X-Access-Token': this.accessToken }
            })
            .then((response) => {
                this.summary = response.data
            })
            .catch((error) => {
                if (error.response && error.response.status === 404 && !this.selectedTeacherId && this.teachers.length) {
                    this.selectedTeacherId = this.teachers[0].id
                    return
                }
                this.errorMessage = error.response ? error.response.data.message : 'APP is temporarily unavailable. Wait or Contact us!'
            })
            .finally(() => {
                this.fetchingContent = false
            })
        },
        formatMonthLabel(month) {
            return moment(month, 'YYYY-MM').format('MMMM YYYY')
        },
        formatDate(date) {
            return moment(date).format('DD MMM (ddd)')
        },
        formatTime(time) {
            return moment(time, 'HH:mm:ss').format('h:mm A')
        },
    },
}
</script>

<style scoped>
</style>
